/**
 * iter235 · FACTORY RULES — "where to add the rules?", answered.
 *
 * The Factory Head's rulebook lives here as data, not inside an AI prompt:
 *   • type a rule in plain English → GPT reads it → confirm → saved
 *   • or fill the structured form
 *   • every rule WARNS on the planning sheet, never blocks
 *   • Hey TI reads the same rules before it recommends anything
 *
 * Backend: /api/manufacturing-rules
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Sparkles, Plus, Pencil, Trash2, RotateCw, AlertTriangle, ShieldCheck,
  ListOrdered, Droplets, Package, Truck, Gauge, CalendarOff, MessageSquare,
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import RuleForm from './RuleForm';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const ICONS = {
  sequence: ListOrdered, wash: Droplets, buffer: Package,
  procurement: Truck, capacity: Gauge, calendar: CalendarOff,
};

const askHeyTi = (q) => {
  try { window.dispatchEvent(new CustomEvent('heyti:open', { detail: { message: q } })); }
  catch { /* no-op */ }
};

const summarise = (r) => {
  const p = r.params || {};
  switch (r.kind) {
    case 'sequence':
      return `${(p.before || []).join(' / ')} → then ${(p.after || []).join(' / ')}`
        + (p.same_line ? ' · same line' : '') + (p.line_no ? ` · Line ${p.line_no}` : '');
    case 'wash':
      return p.mode === 'skip_wash'
        ? `${(p.from_products || []).join(' / ') || 'Any product'} ${p.consecutive_days}+ days in a row · no wash`
        : `After ${(p.from_products || []).join(' / ') || 'anything'} → wash before ${(p.to_products || []).join(' / ') || 'the next product'}`;
    case 'buffer':
      return `${p.item_kind === 'rm' ? 'Raw material' : 'Finished goods'} · ${p.item_name || 'every item'} · min `
        + (p.min_kg ? `${p.min_kg} kg` : p.item_kind === 'rm' ? 'reorder level' : 'FG buffer limit');
    case 'procurement':
      return `${p.material_name || 'Any short material'} · order ${p.lead_days} day(s) early`;
    case 'capacity':
      return `${p.line_no ? `Line ${p.line_no}` : 'Every line'} · ${p.shift || 'any shift'} · max `
        + [p.max_batches ? `${p.max_batches} batch(es)` : '', p.max_kg ? `${p.max_kg} kg` : ''].filter(Boolean).join(' / ');
    default:
      return [(p.weekdays || []).map(w => w[0].toUpperCase() + w.slice(1)).join(', '),
        (p.dates || []).join(', ')].filter(Boolean).join(' · ');
  }
};

export default function FactoryRules() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [text, setText] = useState('');
  const [parsing, setParsing] = useState(false);
  const [draft, setDraft] = useState(null);
  const [editor, setEditor] = useState(null); // {kind, rule?}
  const [saving, setSaving] = useState(false);
  const [names, setNames] = useState({ products: [], materials: [] });
  const [checks, setChecks] = useState(null);
  const [showChecks, setShowChecks] = useState(true);

  const canEdit = !!data?.can_edit;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [r, v] = await Promise.all([
        axios.get(`${API}/manufacturing-rules/`, { withCredentials: true }),
        axios.get(`${API}/manufacturing-rules/violations`, { params: { days: 14 }, withCredentials: true }),
      ]);
      setData(r.data);
      setChecks(v.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not load the rulebook');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/inventory/finished-goods`, { withCredentials: true }).catch(() => ({ data: [] })),
      axios.get(`${API}/inventory/raw-materials`, { withCredentials: true }).catch(() => ({ data: [] })),
    ]).then(([fg, rm]) => setNames({
      products: (fg.data || []).map(x => x.name).filter(Boolean),
      materials: (rm.data || []).map(x => x.name).filter(Boolean),
    }));
  }, []);

  const parse = async () => {
    if (!text.trim()) { toast.error('Type the rule first'); return; }
    setParsing(true);
    try {
      const res = await axios.post(`${API}/manufacturing-rules/parse`, { text }, { withCredentials: true });
      setDraft(res.data);
      toast.success(`Read as a ${res.data.kind} rule`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not read that rule');
    } finally { setParsing(false); }
  };

  const saveRule = async (payload, ruleId) => {
    setSaving(true);
    try {
      if (ruleId) {
        await axios.patch(`${API}/manufacturing-rules/${ruleId}`, payload, { withCredentials: true });
        toast.success('Rule updated');
      } else {
        await axios.post(`${API}/manufacturing-rules/`, payload, { withCredentials: true });
        toast.success('Rule saved · the planning sheet will start warning on it');
      }
      setEditor(null); setDraft(null); setText('');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not save the rule');
    } finally { setSaving(false); }
  };

  const toggle = async (rule) => {
    try {
      await axios.patch(`${API}/manufacturing-rules/${rule.id}`, { enabled: !rule.enabled }, { withCredentials: true });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not change the rule'); }
  };

  const remove = async (rule) => {
    if (!window.confirm(`Delete this rule?\n\n${rule.title}`)) return;
    try {
      await axios.delete(`${API}/manufacturing-rules/${rule.id}`, { withCredentials: true });
      toast.success('Rule deleted');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not delete'); }
  };

  const byKind = useMemo(() => {
    const m = {};
    (data?.rules || []).forEach(r => { (m[r.kind] = m[r.kind] || []).push(r); });
    return m;
  }, [data]);

  const warnFlags = (checks?.plan_flags || []).filter(f => f.level === 'warn');
  const warnAlerts = (checks?.alerts || []).filter(a => a.level === 'warn');

  return (
    <div className="space-y-3" data-testid="fp-rules">
      {/* Header */}
      <div className="bg-zinc-900/40 border border-zinc-800 rounded-xl p-3 flex flex-wrap items-center gap-3">
        <div className="flex-1 min-w-[240px]">
          <p className="font-heading text-sm text-zinc-50 flex items-center gap-2">
            <ShieldCheck size={15} className="text-amber-400" /> Manufacturing rules
          </p>
          <p className="text-[11px] text-zinc-500">
            Your rulebook. Every rule <span className="text-amber-300">warns</span> on the planning sheet and Hey TI
            reads all of them before it recommends anything — nothing is ever blocked or auto-changed.
          </p>
        </div>
        <span className="text-[11px] text-zinc-400">{data?.rules?.length ?? 0} rule(s)</span>
        <button onClick={() => askHeyTi('Check this week’s production plan against my manufacturing rules and tell me what to fix.')}
          data-testid="fp-rules-ask-ti"
          className="ti-btn-ghost text-[11px] px-2.5 py-1.5 rounded-lg flex items-center gap-1">
          <MessageSquare size={12} /> Ask Hey TI
        </button>
        <button onClick={load} data-testid="fp-rules-refresh" className="text-zinc-500 hover:text-zinc-200 p-1.5">
          <RotateCw size={13} />
        </button>
      </div>

      {!canEdit && !loading && (
        <p className="text-[11px] text-orange-300 bg-orange-500/10 border border-orange-500/25 rounded-lg px-3 py-2">
          You can read the rulebook. Only the Factory Head (owner / admin) can add or change rules.
        </p>
      )}

      {/* Live rule check */}
      {checks && (warnFlags.length > 0 || warnAlerts.length > 0) && (
        <div className="bg-red-500/[0.07] border border-red-500/30 rounded-xl p-3" data-testid="fp-rules-violations">
          <button onClick={() => setShowChecks(x => !x)} className="w-full flex items-center gap-2 text-left">
            <AlertTriangle size={14} className="text-red-400" />
            <span className="text-[12px] text-red-200 font-semibold">
              {[warnFlags.length ? `${warnFlags.length} slot(s)` : '',
                warnAlerts.length ? `${warnAlerts.length} stock alert(s)` : '']
                .filter(Boolean).join(' and ')} break a rule in the next 14 days
            </span>
            <span className="flex-1" />
            <span className="text-[10px] text-zinc-400">{showChecks ? 'hide' : 'show'}</span>
          </button>
          {showChecks && (
            <div className="mt-2 space-y-1.5">
              {warnFlags.slice(0, 12).map((f, i) => (
                <p key={i} className="text-[11px] text-zinc-300" data-testid={`fp-rules-flag-${i}`}>
                  <span className="text-red-300 font-mono">{f.plan_id}</span> · {f.text}
                  {f.action && <span className="text-zinc-500"> → {f.action}</span>}
                </p>
              ))}
              {warnAlerts.map((a, i) => (
                <p key={`a${i}`} className="text-[11px] text-orange-200" data-testid={`fp-rules-alert-${i}`}>
                  <span className="text-orange-300">{a.title}</span> · {a.text}
                </p>
              ))}
            </div>
          )}
        </div>
      )}
      {checks && warnFlags.length === 0 && warnAlerts.length === 0 && !loading && (
        <p className="text-[11px] text-emerald-300 bg-emerald-500/[0.07] border border-emerald-500/25 rounded-lg px-3 py-2 flex items-center gap-2"
          data-testid="fp-rules-clean">
          <ShieldCheck size={13} /> Nothing in the next 14 days breaks a rule ({checks.rules_evaluated} rule(s) checked).
        </p>
      )}

      {/* Plain-English capture */}
      {canEdit && (
        <div className="bg-zinc-900/60 border border-zinc-800 rounded-xl p-3.5 space-y-3">
          <p className="text-[12px] text-zinc-200 flex items-center gap-1.5">
            <Sparkles size={13} className="text-amber-400" /> Write a rule the way you'd say it on the floor
          </p>
          <div className="flex flex-col sm:flex-row gap-2">
            <input value={text} onChange={e => setText(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') parse(); }}
              data-testid="fp-rules-ai-input"
              placeholder="e.g. don't make chocolate right after garlic without washing the line"
              className="flex-1 bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-[12px] text-zinc-100 focus:outline-none focus:border-amber-500" />
            <button onClick={parse} disabled={parsing} data-testid="fp-rules-ai-parse"
              className="bg-amber-500 hover:bg-amber-400 disabled:opacity-50 text-black text-[12px] font-semibold px-3.5 py-2 rounded-lg flex items-center justify-center gap-1.5">
              <Sparkles size={13} /> {parsing ? 'Reading…' : 'Read it with AI'}
            </button>
          </div>

          {draft && (
            <div className="border border-amber-500/40 bg-amber-500/[0.07] rounded-xl p-3 space-y-2" data-testid="fp-rules-draft">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-[10px] uppercase tracking-wider bg-amber-500 text-black font-bold px-1.5 py-0.5 rounded">
                  {draft.kind}
                </span>
                <span className="text-[12px] text-zinc-100">{draft.title}</span>
                {draft.confidence && (
                  <span className={`text-[10px] ${draft.confidence === 'high' ? 'text-emerald-300' : 'text-orange-300'}`}>
                    {draft.confidence} confidence
                  </span>
                )}
              </div>
              {draft.note && <p className="text-[11px] text-zinc-400">{draft.note}</p>}
              <p className="text-[11px] text-zinc-300">{summarise(draft)}</p>
              <div className="flex items-center gap-2">
                <button onClick={() => saveRule({ kind: draft.kind, title: draft.title, params: draft.params, source_text: text })}
                  disabled={saving} data-testid="fp-rules-ai-save"
                  className="bg-emerald-600 hover:bg-emerald-500 text-white text-[11px] font-semibold px-3 py-1.5 rounded-lg">
                  {saving ? 'Saving…' : 'Looks right · save it'}
                </button>
                <button onClick={() => { setEditor({ kind: draft.kind, rule: { ...draft, id: null } }); setDraft(null); }}
                  data-testid="fp-rules-ai-tweak" className="ti-btn-ghost text-[11px] px-3 py-1.5 rounded-lg">
                  Tweak the details
                </button>
                <button onClick={() => setDraft(null)} data-testid="fp-rules-ai-discard"
                  className="text-[11px] text-zinc-500 hover:text-zinc-300 px-2">Discard</button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Sections */}
      {loading && <p className="text-center text-zinc-500 text-[12px] py-8">Loading the rulebook…</p>}
      {!loading && (data?.kinds || []).map(meta => {
        const Icon = ICONS[meta.kind] || ShieldCheck;
        const rules = byKind[meta.kind] || [];
        const editingHere = editor && editor.kind === meta.kind;
        return (
          <div key={meta.kind} className="bg-zinc-900/40 border border-zinc-800 rounded-xl overflow-hidden"
            data-testid={`fp-rules-section-${meta.kind}`}>
            <div className="px-3.5 py-2.5 bg-zinc-800/40 border-b border-zinc-800 flex items-center gap-2.5 flex-wrap">
              <Icon size={14} className="text-amber-400" />
              <span className="text-[12px] font-heading text-zinc-50">{meta.label}</span>
              <span className="text-[10px] text-zinc-500">{rules.length}</span>
              <span className="text-[11px] text-zinc-500 hidden md:inline">· {meta.description}</span>
              <span className="flex-1" />
              {canEdit && (
                <button onClick={() => setEditor({ kind: meta.kind })} data-testid={`fp-rules-add-${meta.kind}`}
                  className="text-[11px] text-amber-300 hover:text-amber-200 border border-amber-500/30 rounded-lg px-2 py-1 flex items-center gap-1">
                  <Plus size={11} /> Add
                </button>
              )}
            </div>

            {editingHere && (
              <div className="px-3.5 py-3 border-b border-zinc-800 bg-zinc-900/70">
                <p className="text-[11px] text-zinc-500 mb-2">
                  {editor.rule?.id ? `Editing ${editor.rule.id}` : `New ${meta.label.toLowerCase()} rule`} · e.g. {meta.example}
                </p>
                <RuleForm kind={meta.kind} seed={editor.rule || {}} saving={saving}
                  products={names.products} materials={names.materials}
                  onCancel={() => setEditor(null)}
                  onSave={(payload) => saveRule({ ...payload, source_text: editor.rule?.source_text || '' }, editor.rule?.id)} />
              </div>
            )}

            {rules.length === 0 && !editingHere && (
              <p className="px-3.5 py-3 text-[11px] text-zinc-600">
                No {meta.label.toLowerCase()} rule yet. {canEdit ? `Add one — e.g. “${meta.example}”` : ''}
              </p>
            )}

            {rules.map(r => (
              <div key={r.id} data-testid={`fp-rule-row-${r.id}`}
                className={`px-3.5 py-2.5 border-b border-zinc-800/60 last:border-0 flex items-start gap-3 ${r.enabled ? '' : 'opacity-50'}`}>
                <button onClick={() => canEdit && toggle(r)} disabled={!canEdit}
                  title={canEdit ? (r.enabled ? 'Active — click to pause' : 'Paused — click to activate')
                    : 'Only the Factory Head can change rules'}
                  data-testid={`fp-rule-toggle-${r.id}`}
                  className={`mt-0.5 w-9 h-5 rounded-full shrink-0 transition-colors ${r.enabled ? 'bg-emerald-600' : 'bg-zinc-700'} ${canEdit ? '' : 'cursor-not-allowed opacity-40 grayscale'}`}>
                  <span className={`block w-4 h-4 bg-white rounded-full transition-transform ${r.enabled ? 'translate-x-4' : 'translate-x-0.5'}`} />
                </button>
                <div className="flex-1 min-w-0">
                  <p className="text-[12px] text-zinc-100">{r.title}</p>
                  <p className="text-[11px] text-zinc-400 font-mono">{summarise(r)}</p>
                  {r.source_text && <p className="text-[10px] text-zinc-600 italic mt-0.5">“{r.source_text}”</p>}
                  <p className="text-[10px] text-zinc-600 mt-0.5">
                    {r.id}{r.seeded ? ' · starter rule — edit or delete it' : r.created_by ? ` · by ${r.created_by}` : ''}
                  </p>
                </div>
                {canEdit && (
                  <div className="flex items-center gap-1 shrink-0">
                    <button onClick={() => setEditor({ kind: r.kind, rule: r })} data-testid={`fp-rule-edit-${r.id}`}
                      className="p-1.5 rounded text-zinc-500 hover:text-zinc-100 hover:bg-zinc-700/60"><Pencil size={12} /></button>
                    <button onClick={() => remove(r)} data-testid={`fp-rule-delete-${r.id}`}
                      className="p-1.5 rounded text-red-400 hover:bg-red-500/15"><Trash2 size={12} /></button>
                  </div>
                )}
              </div>
            ))}
          </div>
        );
      })}
    </div>
  );
}
